import { makeAutoObservable } from "mobx"
import { FilterRange, SelectedFilters } from "@cruisen/react-shared"
import { CabinGradeType, TripSearchResultTag } from "@cruisen/contract"

function toggleSelection<T>(list: T[], item: T): T[] {
  if (list.includes(item)) {
    return list.filter((i) => i !== item)
  }
  return [...list, item]
}

export class CruisesFilteringModalStore {
  priceFilters: FilterRange
  shipYearFilters: FilterRange

  selectedDurations: string[]
  selectedShips: string[]
  selectedCruiseLines: string[]
  selectedOriginPorts: string[]
  selectedDestinationPorts: string[]
  selectedAgentLikedCruise: boolean
  selectedTags?: TripSearchResultTag[]
  selectedStartDate: string | null
  selectedEndDate: string | null
  selectedCabinGradeTypes: CabinGradeType[]
  isScrollViewEnabled: boolean
  visibleModal: null | "originPorts" | "destinationPorts"

  constructor(currentFilters: SelectedFilters | null) {
    this.resetBaseFilters(currentFilters)
    this.isScrollViewEnabled = true
    this.visibleModal = null

    makeAutoObservable(this)
  }

  setSelectedCabinGradeTypes(value: CabinGradeType) {
    this.selectedCabinGradeTypes = toggleSelection(this.selectedCabinGradeTypes, value)
  }

  setVisibleModal(value: null | "originPorts" | "destinationPorts") {
    this.visibleModal = value
  }

  setIsScrollViewEnabled(value: boolean) {
    this.isScrollViewEnabled = value
  }

  setSelectedOriginPorts(value: string[]) {
    this.selectedOriginPorts = value
  }

  setSelectedDestinationPorts(value: string[]) {
    this.selectedDestinationPorts = value
  }

  toggleSelectedDurations(duration: string) {
    this.selectedDurations = toggleSelection(this.selectedDurations, duration)
  }

  toggleSelectedShips(shipName: string) {
    this.selectedShips = toggleSelection(this.selectedShips, shipName)
  }

  toggleSelectedCruiseLines(cruiseLine: string) {
    this.selectedCruiseLines = toggleSelection(this.selectedCruiseLines, cruiseLine)
  }

  changeFilters(
    low: number,
    high: number,
    type: "price" | "year",
    range: { min: number; max: number },
  ) {
    let filters = { low, high }

    if (low === range.min && high === range.max) {
      filters = { low: null, high: null }
    }

    if (type === "price") {
      this.priceFilters = filters
    } else {
      this.shipYearFilters = filters
    }
  }

  toggleSelectedAgentLikedCruise() {
    this.selectedAgentLikedCruise = !this.selectedAgentLikedCruise
  }

  toggleTagSelection(tag: TripSearchResultTag) {
    this.selectedTags = toggleSelection(this.selectedTags, tag) as TripSearchResultTag[]
  }

  setSelectedStartDate(value: string | null) {
    this.selectedStartDate = value
  }

  setSelectedEndDate(value: string | null) {
    this.selectedEndDate = value
  }

  resetFilters() {
    this.resetBaseFilters()
  }

  private resetBaseFilters(currentFilters?: SelectedFilters | null) {
    if (currentFilters) {
      this.priceFilters = {
        low: currentFilters.priceFilters.low,
        high: currentFilters.priceFilters.high,
      }
      this.selectedDurations = currentFilters.selectedDurations
      this.selectedShips = currentFilters.selectedShips
      this.selectedCruiseLines = currentFilters.selectedCruiseLines
      this.selectedOriginPorts = currentFilters.selectedOriginPorts
      this.selectedDestinationPorts = currentFilters.selectedDestinationPorts
      this.shipYearFilters = {
        low: currentFilters.shipYearFilters.low,
        high: currentFilters.shipYearFilters.high,
      }
      this.selectedAgentLikedCruise = currentFilters.selectedAgentLikedCruise
      this.selectedTags = currentFilters.selectedTags
      this.selectedStartDate = currentFilters.selectedStartDate
      this.selectedEndDate = currentFilters.selectedEndDate
      this.selectedCabinGradeTypes = currentFilters.selectedCabinGradeTypes || []
    } else {
      this.priceFilters = { low: null, high: null }
      this.selectedDurations = []
      this.selectedShips = []
      this.selectedCruiseLines = []
      this.selectedOriginPorts = []
      this.selectedDestinationPorts = []
      this.shipYearFilters = {
        low: null,
        high: null,
      }
      this.selectedAgentLikedCruise = false
      this.selectedTags = []
      this.selectedStartDate = null
      this.selectedEndDate = null
      this.selectedCabinGradeTypes = []
    }
  }

  get selectedFilters() {
    return {
      priceFilters: this.priceFilters,
      selectedShips: this.selectedShips,
      selectedCruiseLines: this.selectedCruiseLines,
      shipYearFilters: this.shipYearFilters,
      selectedDurations: this.selectedDurations,
      selectedOriginPorts: this.selectedOriginPorts,
      selectedDestinationPorts: this.selectedDestinationPorts,
      selectedTags: this.selectedTags,
      selectedAgentLikedCruise: this.selectedAgentLikedCruise,
      selectedStartDate: this.selectedStartDate,
      selectedEndDate: this.selectedEndDate,
      selectedCabinGradeTypes: this.selectedCabinGradeTypes,
    }
  }
}
